<template>
  <v-card flat class="pa-0 mt-4">
    <div
      @click="isCollapsed = !isCollapsed"
      class="d-flex justify-space-between align-center cursor-pointer grey lighten-4 pa-3 rounded mb-2"
      style="cursor: pointer"
    >
      <span class="text-subtitle-1 font-weight-bold">Salary Breakdown</span>
      <v-icon>{{ isCollapsed ? 'mdi-chevron-down' : 'mdi-chevron-up' }}</v-icon>
    </div>
    <v-expand-transition>
      <div v-show="!isCollapsed">
        <div class="flex gap-4">
          <!-- Earnings Section -->
          <div class="w-full border">
            <div class="earnings-header pa-2 text-center">
              <h2 class="text-md font-weight-bold white--text">EARNINGS</h2>
            </div>
            <div class="pa-4">
              <div
                v-for="(earning, index) in earnings"
                :key="index"
                class="d-flex justify-space-between align-center py-3"
                :class="{ 'dotted-line': index < earnings.length - 1 }"
              >
                <span class="text-sm">{{ earning.label || earning.name }}</span>
                <span class="text-sm font-weight-medium">₹ {{ formatAmount(earning.amount) }}</span>
              </div>
              <v-divider class="mb-3"></v-divider>
              <div class="d-flex justify-space-between align-center py-2 grey lighten-3 px-3">
                <span class="text-base font-weight-bold">GROSS EARNINGS</span>
                <span class="text-base font-weight-bold">₹ {{ formatAmount(grossEarnings) }}</span>
              </div>
            </div>
          </div>

          <!-- Deductions Section -->
          <div class="w-full border">
            <div class="deductions-header pa-2 text-center">
              <h2 class="text-md font-weight-bold white--text">DEDUCTIONS</h2>
            </div>
            <div class="pa-4">
              <div
                v-for="(deduction, index) in deductions"
                :key="index"
                class="d-flex justify-space-between align-center py-3"
                :class="{ 'dotted-line': index < deductions.length - 1 }"
              >
                <span class="text-sm">{{ deduction.label || deduction.name }}</span>
                <span class="text-sm font-weight-medium"
                  >₹ {{ formatAmount(deduction.amount) }}</span
                >
              </div>
              <v-divider class="mb-3"></v-divider>
              <div class="d-flex justify-space-between align-center py-2 grey lighten-3 px-3">
                <span class="text-base font-weight-bold">TOTAL DEDUCTIONS</span>
                <span class="text-base font-weight-bold"
                  >₹ {{ formatAmount(totalDeductions) }}</span
                >
              </div>
            </div>
          </div>
        </div>

        <!-- Net Salary Section -->
        <div class="net-salary-box ma-2 pa-4 text-center rounded">
          <h1 class="text-md font-weight-bold green--text text--darken-2 mb-2">
            NET SALARY: ₹ {{ formatAmount(netSalary) }}
          </h1>
          <p class="text-body-1 grey--text text--darken-1 mb-0">
            {{ numberToWords(netSalary) }} Rupees Only
          </p>
        </div>
      </div>
    </v-expand-transition>
  </v-card>
</template>

<script>
export default {
  name: 'SalaryBreakdown',
  data() {
    return {
      isCollapsed: true
    }
  },
  props: {
    earnings: {
      type: Array,
      default: () => []
    },
    deductions: {
      type: Array,
      default: () => []
    },
    grossEarnings: {
      type: Number,
      default: 0
    },
    totalDeductions: {
      type: Number,
      default: 0
    },
    netSalary: {
      type: String,
      default: '0'
    }
  },
  methods: {
    formatAmount(amount) {
      // Convert to number if it's a string
      const num = typeof amount === 'string' ? parseFloat(amount) : amount
      // Return formatted amount with commas and 2 decimal places
      return isNaN(num)
        ? '0.00'
        : num.toLocaleString('en-IN', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          })
    },
    numberToWords(num) {
      const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine']
      const teens = [
        'Ten',
        'Eleven',
        'Twelve',
        'Thirteen',
        'Fourteen',
        'Fifteen',
        'Sixteen',
        'Seventeen',
        'Eighteen',
        'Nineteen'
      ]
      const tens = [
        '',
        '',
        'Twenty',
        'Thirty',
        'Forty',
        'Fifty',
        'Sixty',
        'Seventy',
        'Eighty',
        'Ninety'
      ]

      // Convert to integer to avoid decimal issues
      num = Math.round(parseFloat(num) || 0)

      if (num === 0) return 'Zero'

      function convertHundreds(n) {
        let result = ''
        if (n >= 100) {
          result += ones[Math.floor(n / 100)] + ' Hundred '
          n %= 100
        }
        if (n >= 20) {
          result += tens[Math.floor(n / 10)] + ' '
          n %= 10
        } else if (n >= 10) {
          result += teens[n - 10] + ' '
          return result.trim()
        }
        if (n > 0) {
          result += ones[n] + ' '
        }
        return result.trim()
      }

      let result = ''
      let crore = Math.floor(num / 10000000)
      if (crore > 0) {
        result += convertHundreds(crore) + ' Crore '
        num %= 10000000
      }

      let lakh = Math.floor(num / 100000)
      if (lakh > 0) {
        result += convertHundreds(lakh) + ' Lakh '
        num %= 100000
      }

      let thousand = Math.floor(num / 1000)
      if (thousand > 0) {
        result += convertHundreds(thousand) + ' Thousand '
        num %= 1000
      }

      if (num > 0) {
        result += convertHundreds(num)
      }

      return result.trim()
    }
  }
}
</script>

<style scoped>
.earnings-header {
  background-color: #4a4a4a;
  color: white;
}

.deductions-header {
  background-color: #4a4a4a;
  color: white;
}

.net-salary-box {
  border: 2px solid #4caf50;
  background-color: #f1f8e9;
}

.dotted-line {
  border-bottom: 1px dotted #ccc;
}
</style>
